import React from 'react';
import classNames from 'classnames';
import { RCLightbox } from '@reverbdotcom/cadence/components';

import isNode from '../is-node';
import LightboxImageItem from './lightbox_image_item';
import {
  LightboxImageControls,
  LightboxImageThumbnails,
  LightboxIndexChangeSource,
  Swipeable,
  handleArrowKeyDown,
  isLink,
  nextIndex,
  previousIndex,
  scrollThumbnailRow,
  useWindowArrowKeys,
} from './lightbox_image_parts';

export interface LightboxViewerItem {
  // a full-size image URL, or a YouTube slug for a video
  src: string;
  thumbnail?: string;
  // a smaller copy of an image, already loaded or quicker to load, to show until the full-size one has
  preview?: string;
  alt?: string;
}

// What every full-screen viewer for LightboxImage implements.
export interface LightboxViewerProps {
  items: LightboxViewerItem[];
  index: number;
  // called whenever the user picks an item, even the one already showing
  onIndexChange: (index: number, details: { source: LightboxIndexChangeSource }) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  // names the viewer for screen readers
  label: string;
}

interface Props extends LightboxViewerProps {
  id?: string;
  caption?: string;
  disableImageOverlay?: boolean;
}

export default function LightboxImageViewer({
  items,
  index,
  onIndexChange,
  isOpen,
  onOpenChange,
  label,
  id,
  caption,
  disableImageOverlay,
}: Props) {
  const [displayVideo, setDisplayVideo] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(false);
  const [hasLoadedImage, setHasLoadedImage] = React.useState(false);
  const [wasOpen, setWasOpen] = React.useState(isOpen);
  // held in state, not a ref: the row mounts a render after opening, once the frame's portal is ready
  const [thumbsRow, setThumbsRow] = React.useState<HTMLDivElement | null>(null);

  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setDisplayVideo(false);
  }

  const isSingleItem = items.length === 1;
  const showThumbnails = items.every(item => item.thumbnail);

  React.useEffect(() => {
    if (thumbsRow) {
      scrollThumbnailRow(thumbsRow, index);
    }
  }, [thumbsRow, index]);

  function loadNext(e) {
    e.stopPropagation();
    setDisplayVideo(false);
    setImageLoading(true);
    onIndexChange(nextIndex(index, items.length), { source: 'next' });
  }

  function loadPrevious(e) {
    e.stopPropagation();
    setDisplayVideo(false);
    setImageLoading(true);
    onIndexChange(previousIndex(index, items.length), { source: 'previous' });
  }

  const arrowKeyHandlers = { onPrevious: loadPrevious, onNext: loadNext };

  useWindowArrowKeys(isOpen && !isSingleItem, arrowKeyHandlers);

  function handleKeyDown(e) {
    if (isSingleItem) return;
    handleArrowKeyDown(e, arrowKeyHandlers);
  }

  function handleImageLoaded() {
    setHasLoadedImage(true);
    setImageLoading(false);
  }

  function handleImageClick(evt) {
    evt.stopPropagation();

    if (!isLink(items[index].src)) {
      setDisplayVideo(true);
    }
  }

  function handleThumbClick(evt, idx: number) {
    evt.preventDefault();
    setDisplayVideo(false);
    onIndexChange(idx, { source: 'thumbnail' });
  }

  const item = items[index];

  const image = (
    <LightboxImageItem
      urlOrSlug={item?.src}
      alt={item?.alt}
      onLoad={handleImageLoaded}
      onClick={handleImageClick}
      renderVideo={displayVideo}
      index={index}
      disableImageOverlay={disableImageOverlay}
    />
  );

  return (
    <>
      {hasLoadedImage &&
        <div className="d-none">
          <div data-lightbox-fullsize-preload>
            {items.filter(({ src }) => isLink(src)).map(({ src, alt }, i) => (
              <img src={src} key={i} alt={alt || ''} />
            ))}
          </div>
        </div>
      }
      {!isNode &&
        <RCLightbox
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          label={label}
        >
          <div
            id={id}
            className={classNames(
              'lightbox-modal',
              { 'lightbox-modal--image-loading': imageLoading },
            )}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="lightbox-modal__primary"
              onKeyDown={handleKeyDown}
            >
              {!!caption?.length &&
                <div className="lightbox-modal__caption">
                  {caption}
                </div>
              }
              {isSingleItem ? image : (
                <Swipeable
                  onSwipedLeft={loadNext}
                  onSwipedRight={loadPrevious}
                >
                  {image}
                </Swipeable>
              )}
            </div>
            {showThumbnails &&
              <LightboxImageThumbnails
                ref={setThumbsRow}
                urls={items.map(({ thumbnail }) => thumbnail)}
                alts={items.map(({ alt }) => alt)}
                activeIndex={index}
                disableImageOverlay={disableImageOverlay}
                onThumbClick={handleThumbClick}
              />
            }
            {!isSingleItem &&
              <LightboxImageControls
                size="medium"
                onPrevious={loadPrevious}
                onNext={loadNext}
              />
            }
          </div>
        </RCLightbox>
      }
    </>
  );
}
